import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { useWebSocket } from "@/hooks/useWebSocket";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Activity, Ear, Bot, Users, RefreshCw, ChevronLeft, Terminal,
  Radio, Phone, Clock, Eye, Pause, X, FileText, Download,
  ChevronRight, Headphones, Zap, Wifi, WifiOff
} from "lucide-react";
import { toast } from "sonner";

const FREQUENCIES = [250, 500, 1000, 2000, 4000, 8000];
const fmtFreq = (f) => (f >= 1000 ? `${f / 1000}k` : f);

const statusConfig = {
  created: { label: "Waiting", color: "bg-amber-500/10 text-amber-600 border-amber-500/20", dot: "bg-amber-400" },
  patient_joined: { label: "Joined", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", dot: "bg-emerald-500" },
  instructions: { label: "Instructions", color: "bg-blue-500/10 text-blue-600 border-blue-500/20", dot: "bg-blue-400" },
  calibration: { label: "Calibrating", color: "bg-violet-500/10 text-violet-600 border-violet-500/20", dot: "bg-violet-400" },
  testing_right: { label: "Testing R", color: "bg-red-500/10 text-red-600 border-red-500/20", dot: "bg-red-500" },
  testing_left: { label: "Testing L", color: "bg-sky-500/10 text-sky-600 border-sky-500/20", dot: "bg-sky-500" },
  completed: { label: "Completed", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", dot: "bg-emerald-500" },
};

function timeSince(dateStr) {
  if (!dateStr) return "--";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

// ─── Audiogram Component (Canvas-based for reliable rendering) ───
function Audiogram({ thresholds, currentFrequency, currentEar, size = "full" }) {
  const canvasRef = useRef(null);
  const right = thresholds?.right || {};
  const left = thresholds?.left || {};

  const isSmall = size === "mini";
  const W = isSmall ? 260 : 520;
  const H = isSmall ? 120 : 320;
  const PAD_L = isSmall ? 30 : 50;
  const PAD_R = isSmall ? 10 : 20;
  const PAD_T = isSmall ? 10 : 25;
  const PAD_B = isSmall ? 20 : 30;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;

    const plotW = W - PAD_L - PAD_R;
    const plotH = H - PAD_T - PAD_B;
    const dbMin = -10;
    const dbMax = 120;
    const dbRange = dbMax - dbMin;

    const freqX = (f) => PAD_L + (FREQUENCIES.indexOf(f) / (FREQUENCIES.length - 1)) * plotW;
    const dbY = (db) => PAD_T + ((db - dbMin) / dbRange) * plotH;

    // Background
    ctx.fillStyle = isSmall ? "#f8fafc" : "#ffffff";
    ctx.fillRect(0, 0, W, H);

    // Grid lines
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 0.5;
    const dbSteps = isSmall ? [0, 20, 40, 60, 80, 100, 120] : [-10, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120];
    dbSteps.forEach((db) => {
      const y = dbY(db);
      ctx.beginPath();
      ctx.moveTo(PAD_L, y);
      ctx.lineTo(W - PAD_R, y);
      ctx.stroke();
    });
    FREQUENCIES.forEach((f) => {
      const x = freqX(f);
      ctx.beginPath();
      ctx.moveTo(x, PAD_T);
      ctx.lineTo(x, H - PAD_B);
      ctx.stroke();
    });

    // Normal hearing zone (green dashed line at 25 dB)
    if (!isSmall) {
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      const normalY = dbY(25);
      ctx.beginPath();
      ctx.moveTo(PAD_L, normalY);
      ctx.lineTo(W - PAD_R, normalY);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Current frequency highlight
    if (currentFrequency && FREQUENCIES.includes(currentFrequency) && !isSmall) {
      const cx = freqX(currentFrequency);
      ctx.fillStyle = currentEar === "left" ? "rgba(59,130,246,0.08)" : "rgba(239,68,68,0.08)";
      ctx.fillRect(cx - 15, PAD_T, 30, plotH);
    }

    // Y-axis labels
    ctx.fillStyle = "#9ca3af";
    ctx.font = isSmall ? "9px sans-serif" : "11px sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    const labelSteps = isSmall ? [0, 40, 80, 120] : [-10, 0, 20, 40, 60, 80, 100, 120];
    labelSteps.forEach((db) => {
      ctx.fillText(String(db), PAD_L - 4, dbY(db));
    });

    // X-axis labels
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    FREQUENCIES.forEach((f) => {
      ctx.fillText(fmtFreq(f), freqX(f), H - PAD_B + 4);
    });

    // Plot helper
    const plotEar = (earData, color, marker) => {
      const pts = FREQUENCIES.map((f) => {
        const t = earData[f] ?? earData[String(f)];
        if (t === undefined || t === null) return null;
        return { x: freqX(f), y: dbY(t), val: t };
      }).filter(Boolean);
      if (pts.length === 0) return;

      // Line
      if (pts.length > 1) {
        ctx.strokeStyle = color;
        ctx.lineWidth = isSmall ? 1.5 : 2.5;
        ctx.beginPath();
        pts.forEach((p, i) => { i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y); });
        ctx.stroke();
      }

      // Markers
      const r = isSmall ? 3 : 7;
      pts.forEach((p) => {
        if (marker === "circle") {
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fillStyle = "#ffffff";
          ctx.fill();
          ctx.strokeStyle = color;
          ctx.lineWidth = isSmall ? 1.5 : 2.5;
          ctx.stroke();
        } else {
          // X marker for left
          ctx.strokeStyle = color;
          ctx.lineWidth = isSmall ? 1.5 : 2.5;
          ctx.beginPath();
          ctx.moveTo(p.x - r, p.y - r);
          ctx.lineTo(p.x + r, p.y + r);
          ctx.moveTo(p.x + r, p.y - r);
          ctx.lineTo(p.x - r, p.y + r);
          ctx.stroke();
        }
      });
    };

    // Plot right ear (red circles, O)
    plotEar(right, "#ef4444", "circle");
    // Plot left ear (blue X)
    plotEar(left, "#3b82f6", "x");

    // Legend (full size only)
    if (!isSmall) {
      ctx.font = "11px sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      // Right
      ctx.beginPath();
      ctx.arc(W - PAD_R - 120, PAD_T + 8, 5, 0, Math.PI * 2);
      ctx.strokeStyle = "#ef4444";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = "#6b7280";
      ctx.fillText("Right (O)", W - PAD_R - 110, PAD_T + 8);
      // Left
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(W - PAD_R - 125, PAD_T + 26);
      ctx.lineTo(W - PAD_R - 115, PAD_T + 36);
      ctx.moveTo(W - PAD_R - 115, PAD_T + 26);
      ctx.lineTo(W - PAD_R - 125, PAD_T + 36);
      ctx.stroke();
      ctx.fillStyle = "#6b7280";
      ctx.fillText("Left (X)", W - PAD_R - 110, PAD_T + 31);
    }
  }, [thresholds, currentFrequency, currentEar, isSmall, W, H, PAD_L, PAD_R, PAD_T, PAD_B, right, left]);

  const hasData = Object.keys(right).length > 0 || Object.keys(left).length > 0;
  if (!hasData && isSmall) {
    return <div className="h-[120px] flex items-center justify-center text-xs text-gray-400">No data yet</div>;
  }

  return (
    <div className="flex justify-center">
      <canvas ref={canvasRef} style={{ width: W, height: H }} className="rounded-lg" />
    </div>
  );
}

// ─── Main Component ───
export default function LiveMonitorPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pollRef = useRef(null);
  const detailPollRef = useRef(null);
  const logEndRef = useRef(null);
  const initialSessionHandled = useRef(false);

  // Handle ?session= query param for direct navigation from PTALiveTestPage
  useEffect(() => {
    const sessionId = searchParams.get("session");
    if (sessionId && !initialSessionHandled.current) {
      initialSessionHandled.current = true;
      setSelectedId(sessionId);
      setDetailLoading(true);
    }
  }, [searchParams]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await apiClient.get("/pta-live-sessions/active-monitor");
      setSessions(res.data);
    } catch (err) {
      console.error("Monitor fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (id) => {
    try {
      const res = await apiClient.get(`/pta-live-sessions/monitor/${id}`);
      setDetail((prev) => {
        // Only show toast ONCE when status transitions to completed
        if (res.data.status === "completed" && prev?.status !== "completed") {
          toast.success("Test completed!");
          fetchSessions();
        }
        return res.data;
      });
    } catch (err) {
      console.error("Detail fetch error:", err);
    } finally {
      setDetailLoading(false);
    }
  }, [fetchSessions]);

  // ── WebSocket: real-time updates from monitor channel ──
  const handleWsMessage = useCallback((data) => {
    if (data.type === "session_update" || data.type === "threshold" || data.type === "completed") {
      // Refresh session list on any meaningful update
      fetchSessions();
      // If we're watching this specific session in detail, refresh it too
      if (selectedId && data.session_id === selectedId) {
        fetchDetail(selectedId);
      }
    }
  }, [fetchSessions, fetchDetail, selectedId]);

  const { connected: wsConnected } = useWebSocket("/api/ws/monitor", handleWsMessage);

  // Slow polling fallback (10s when WS connected, 3s when disconnected)
  useEffect(() => {
    fetchSessions();
    const interval = wsConnected ? 10000 : 3000;
    pollRef.current = setInterval(fetchSessions, interval);
    return () => clearInterval(pollRef.current);
  }, [fetchSessions, wsConnected]);

  // Detail polling (5s when WS connected, 1.5s when disconnected)
  useEffect(() => {
    if (selectedId) {
      setDetailLoading(true);
      fetchDetail(selectedId);
      const interval = wsConnected ? 5000 : 1500;
      detailPollRef.current = setInterval(() => fetchDetail(selectedId), interval);
    }
    return () => { if (detailPollRef.current) clearInterval(detailPollRef.current); };
  }, [selectedId, fetchDetail, wsConnected]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.ai_test_log]);

  const openSession = (id) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const downloadReport = async (sessionId) => {
    try {
      const res = await apiClient.get(`/pta-live-sessions/${sessionId}/report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PTA_Report_${sessionId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error("Failed to download report");
    }
  };

  const aiSessions = sessions.filter((s) => s.ai_test_active);
  const otherSessions = sessions.filter((s) => !s.ai_test_active);

  // ─── DETAIL VIEW ───
  if (selectedId) {
    const s = detail;
    const isCompleted = s?.status === "completed";

    return (
      <DashboardLayout>
        <div className="p-4 lg:p-6 max-w-7xl mx-auto" data-testid="live-monitor-detail">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="sm" onClick={closeDetail} data-testid="monitor-back-btn"
              className="text-gray-600 hover:text-gray-900">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Monitor
            </Button>
            {s && (
              <div className="flex items-center gap-2">
                {isCompleted && (
                  <Button size="sm" variant="outline" onClick={() => downloadReport(s.id)} data-testid="download-report-detail-btn">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> PDF Report
                  </Button>
                )}
                {s.ai_test_active && !s.ai_test_paused && (
                  <Badge className="bg-violet-500/10 text-violet-700 border border-violet-500/20 gap-1.5">
                    <span className="w-2 h-2 bg-violet-500 rounded-full animate-pulse" /> AI Running
                  </Badge>
                )}
                {s.ai_test_paused && (
                  <Badge className="bg-amber-500/10 text-amber-700 border border-amber-500/20">
                    <Pause className="w-3 h-3 mr-1" />Paused
                  </Badge>
                )}
              </div>
            )}
          </div>

          {detailLoading && !s ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <RefreshCw className="w-8 h-8 animate-spin text-sky-600" />
            </div>
          ) : s ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              {/* Left panel: Session info + Thresholds */}
              <div className="lg:col-span-4 space-y-4">
                {/* Session card */}
                <Card className="border-gray-200">
                  <CardContent className="pt-5 space-y-4">
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">Session Code</p>
                      <p className="text-3xl font-mono font-bold tracking-wider" style={{ color: "#1e3a6e" }}>
                        {s.session_code}
                      </p>
                    </div>
                    <div className="border-t pt-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Patient</span>
                        <span className="font-semibold text-gray-900">{s.patient_name}</span>
                      </div>
                      {s.patient_phone && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Phone</span>
                          <span className="text-gray-700">{s.patient_phone}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Audiologist</span>
                        <span className="text-gray-700">{s.audiologist_name || "AI"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Type</span>
                        <span className="text-gray-700">{s.is_self_test ? "Self-Test (B2C)" : "Clinic"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Headphones</span>
                        <span className="text-gray-700">{s.headphone_type || "Generic"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Started</span>
                        <span className="text-gray-700">{timeSince(s.created_at)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Current state */}
                {!isCompleted && (
                  <Card className="border-gray-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Current Tone</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-2">
                        <div className={`p-3 rounded-xl text-center border ${
                          s.current_ear === "right" ? "bg-red-50 border-red-200" : s.current_ear === "left" ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"
                        }`}>
                          <Ear className={`w-5 h-5 mx-auto mb-1 ${s.current_ear === "right" ? "text-red-500" : s.current_ear === "left" ? "text-blue-500 scale-x-[-1]" : "text-gray-400"}`} />
                          <p className="text-xs font-bold uppercase">{s.current_ear || "--"}</p>
                        </div>
                        <div className="p-3 rounded-xl text-center bg-gray-50 border border-gray-200">
                          <p className="text-lg font-bold text-gray-900">{s.current_frequency ? fmtFreq(s.current_frequency) : "--"}</p>
                          <p className="text-[10px] text-gray-500">Hz</p>
                        </div>
                        <div className="p-3 rounded-xl text-center bg-gray-50 border border-gray-200">
                          <p className="text-lg font-bold text-gray-900">{s.current_decibel ?? "--"}</p>
                          <p className="text-[10px] text-gray-500">dB HL</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Threshold table */}
                <Card className="border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {isCompleted ? "Final Thresholds" : "Thresholds Found"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="threshold-table">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-1 text-gray-500 font-medium">Hz</th>
                            {FREQUENCIES.map((f) => (
                              <th key={f} className="text-center py-1 text-gray-500 font-medium">{fmtFreq(f)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {["right", "left"].map((ear) => (
                            <tr key={ear} className="border-b last:border-0">
                              <td className={`py-1.5 font-bold ${ear === "right" ? "text-red-600" : "text-blue-600"}`}>
                                {ear === "right" ? "R" : "L"}
                              </td>
                              {FREQUENCIES.map((f) => {
                                const val = s.thresholds?.[ear]?.[f] ?? s.thresholds?.[ear]?.[String(f)];
                                return (
                                  <td key={f} className={`text-center py-1.5 font-semibold ${val !== undefined ? "text-gray-900" : "text-gray-300"}`}>
                                    {val ?? "--"}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* PTA averages for completed */}
                    {isCompleted && (s.pta_right != null || s.pta_left != null) && (
                      <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2">
                        <div className="bg-red-50 rounded-lg p-2 text-center">
                          <p className="text-[10px] text-gray-500">Right PTA</p>
                          <p className="text-lg font-bold text-gray-900">{s.pta_right ?? "--"} <span className="text-xs font-normal">dB</span></p>
                          <p className={`text-xs font-medium ${s.severity_right === "Normal" ? "text-green-600" : "text-amber-600"}`}>{s.severity_right}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-2 text-center">
                          <p className="text-[10px] text-gray-500">Left PTA</p>
                          <p className="text-lg font-bold text-gray-900">{s.pta_left ?? "--"} <span className="text-xs font-normal">dB</span></p>
                          <p className={`text-xs font-medium ${s.severity_left === "Normal" ? "text-green-600" : "text-amber-600"}`}>{s.severity_left}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right panel: Audiogram + Log + Progress */}
              <div className="lg:col-span-8 space-y-4">
                {/* Live Audiogram */}
                <Card className="border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Activity className="w-4 h-4" style={{ color: "#1e3a6e" }} />
                      {isCompleted ? "Final Audiogram" : "Live Audiogram"}
                      {s.ai_test_active && !s.ai_test_paused && <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Audiogram thresholds={s.thresholds} currentFrequency={s.current_frequency} currentEar={s.current_ear} size="full" />
                  </CardContent>
                </Card>

                {/* Frequency Progress */}
                {!isCompleted && (
                  <Card className="border-gray-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Frequency Progress</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {[1000, 2000, 4000, 8000, 500, 250].map((freq) => {
                          const rightDone = (s.thresholds?.right?.[freq] ?? s.thresholds?.right?.[String(freq)]) !== undefined;
                          const leftDone = (s.thresholds?.left?.[freq] ?? s.thresholds?.left?.[String(freq)]) !== undefined;
                          const isCurrent = s.current_frequency === freq;
                          return (
                            <div key={freq} className={`px-3 py-2 text-xs font-semibold rounded-xl border-2 transition-all ${
                              isCurrent ? "bg-violet-50 border-violet-400 text-violet-700 ring-2 ring-violet-200 scale-105" :
                              rightDone && leftDone ? "bg-emerald-50 border-emerald-300 text-emerald-700" :
                              rightDone || leftDone ? "bg-amber-50 border-amber-200 text-amber-600" :
                              "bg-gray-50 border-gray-200 text-gray-400"
                            }`}>
                              {fmtFreq(freq)}Hz
                              <div className="flex gap-0.5 mt-1 justify-center">
                                <span className={`w-2 h-2 rounded-full ${rightDone ? "bg-red-500" : "bg-gray-200"}`} />
                                <span className={`w-2 h-2 rounded-full ${leftDone ? "bg-blue-500" : "bg-gray-200"}`} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* AI Log */}
                <Card className="border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Terminal className="w-4 h-4 text-gray-400" />
                      AI Test Log
                      <span className="text-xs text-gray-400 font-normal ml-auto">{s.ai_test_log?.length || 0} entries</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gray-950 rounded-xl p-4 max-h-56 overflow-y-auto font-mono text-xs" data-testid="monitor-ai-log">
                      {(!s.ai_test_log || s.ai_test_log.length === 0) && (
                        <p className="text-gray-600">No AI log entries yet...</p>
                      )}
                      {s.ai_test_log?.map((entry, i) => (
                        <div key={i} className={`py-0.5 ${
                          entry.level === "error" ? "text-red-400" : entry.level === "warn" ? "text-amber-400" : "text-emerald-400"
                        }`}>
                          <span className="text-gray-600 mr-2">{entry.ts?.slice(11, 19) || ""}</span>
                          {entry.msg}
                        </div>
                      ))}
                      <div ref={logEndRef} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : null}
        </div>
      </DashboardLayout>
    );
  }

  // ─── MAIN DASHBOARD ───
  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-6" data-testid="live-monitor-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Radio className="w-5 h-5 text-red-500" />
              Live Monitor
              {sessions.length > 0 && (
                <span className="relative flex h-2.5 w-2.5 ml-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
              )}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">Real-time view of active hearing tests</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-xs px-2 py-0.5 ${wsConnected ? 'border-green-300 text-green-700' : 'border-amber-300 text-amber-700'}`}>
              {wsConnected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
              {wsConnected ? "Live" : "Polling"}
            </Badge>
            <Badge variant="outline" className="text-sm px-3 py-1 border-gray-300">
              <Activity className="w-3.5 h-3.5 mr-1.5 text-green-500" />
              {sessions.length} Active
            </Badge>
            <Button variant="outline" size="sm" onClick={fetchSessions} data-testid="refresh-monitor-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <RefreshCw className="w-8 h-8 animate-spin text-sky-600" />
          </div>
        ) : sessions.length === 0 ? (
          <Card className="border-dashed border-2 border-gray-200">
            <CardContent className="py-20 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Radio className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">No Active Tests</h3>
              <p className="text-gray-400 text-sm max-w-sm mx-auto">When patients start hearing tests, they'll appear here in real-time with live audiograms.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* AI Active Tests */}
            {aiSessions.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Bot className="w-4 h-4 text-violet-600" />
                  <h2 className="text-sm font-bold text-gray-700">AI Tests Running</h2>
                  <Badge className="bg-violet-500/10 text-violet-600 border border-violet-500/20 text-[10px]">{aiSessions.length}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {aiSessions.map((s) => {
                    const sc = statusConfig[s.status] || { label: s.status, color: "bg-gray-100 text-gray-700", dot: "bg-gray-400" };
                    const progress = ((s.right_done + s.left_done) / (s.total_freqs * 2)) * 100;
                    const lastLog = s.ai_test_log?.[s.ai_test_log.length - 1];
                    return (
                      <Card key={s.id}
                        className="hover:shadow-md transition-all cursor-pointer border-gray-200 group"
                        onClick={() => openSession(s.id)}
                        data-testid={`monitor-card-${s.session_code}`}
                      >
                        <CardContent className="pt-4 pb-3 px-4 relative overflow-hidden">
                          {/* Accent bar */}
                          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-violet-500 to-indigo-500" />

                          <div className="flex items-start justify-between mb-2.5">
                            <div>
                              <p className="font-bold text-gray-900 text-sm">{s.patient_name}</p>
                              {s.patient_phone && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                  <Phone className="w-3 h-3" />{s.patient_phone}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Badge className={`${sc.color} text-[10px] border`}>{sc.label}</Badge>
                              {s.is_self_test && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">B2C</Badge>}
                            </div>
                          </div>

                          {/* Current test state */}
                          <div className="flex items-center gap-2 mb-2.5 text-xs">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-bold border ${
                              s.current_ear === "right" ? "bg-red-50 text-red-700 border-red-200" : s.current_ear === "left" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-gray-50 text-gray-500 border-gray-200"
                            }`}>
                              <Ear className={`w-3 h-3 ${s.current_ear === "left" ? "scale-x-[-1]" : ""}`} />
                              {s.current_ear?.toUpperCase() || "--"}
                            </span>
                            <span className="font-mono font-bold text-gray-900">
                              {s.current_frequency ? `${fmtFreq(s.current_frequency)}Hz` : "--"}
                            </span>
                            <span className="text-gray-400">@</span>
                            <span className="font-mono font-bold text-gray-900">{s.current_decibel ?? "--"} dB</span>
                            {s.ai_test_paused && <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-auto"><Pause className="w-2.5 h-2.5 mr-0.5" />Paused</Badge>}
                          </div>

                          {/* Progress bar */}
                          <div className="mb-2.5">
                            <div className="flex justify-between text-[10px] text-gray-400 mb-0.5">
                              <span>Progress</span>
                              <span>{s.right_done + s.left_done}/{s.total_freqs * 2} freq</span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-500"
                                style={{ width: `${progress}%` }} />
                            </div>
                          </div>

                          {/* Mini audiogram */}
                          <Audiogram thresholds={s.thresholds} size="mini" />

                          {/* Last log */}
                          {lastLog && (
                            <div className="mt-2 bg-gray-950 rounded-lg px-3 py-1.5 text-[10px] font-mono text-emerald-400 truncate">
                              {lastLog.ts?.slice(11, 19)} {lastLog.msg}
                            </div>
                          )}

                          <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-gray-100">
                            <span className="text-[10px] text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />{timeSince(s.created_at)}
                            </span>
                            <span className="text-xs text-violet-600 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              View Live <ChevronRight className="w-3 h-3" />
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other Active Sessions */}
            {otherSessions.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-sky-600" />
                  <h2 className="text-sm font-bold text-gray-700">Other Active Sessions</h2>
                  <Badge variant="outline" className="text-[10px]">{otherSessions.length}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {otherSessions.map((s) => {
                    const sc = statusConfig[s.status] || { label: s.status, color: "bg-gray-100 text-gray-700", dot: "bg-gray-400" };
                    return (
                      <Card key={s.id}
                        className="hover:shadow-md transition-all cursor-pointer border-gray-200 group"
                        onClick={() => openSession(s.id)}
                        data-testid={`monitor-card-${s.session_code}`}
                      >
                        <CardContent className="pt-4 pb-3 px-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-bold text-gray-900 text-sm">{s.patient_name}</p>
                              <p className="text-xs text-gray-500">{s.patient_phone || "--"}</p>
                            </div>
                            <Badge className={`${sc.color} text-[10px] border`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot} mr-1`} />{sc.label}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-gray-400">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeSince(s.created_at)}</span>
                            <span>{s.audiologist_name || "--"}</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
